import { queryKeyChatId, queryKeyFlexibleMonths, queryKeyFlexibleNights, queryKeyCheckIn, queryKeyCheckOut } from 'constants/url'
import offerPageURL, { getExperienceOfferPageURL, getHotelOfferUrl } from 'lib/offer/offerPageURL'
import { isExperienceOffer } from 'lib/offer/offerTypes'
import { buildSearchParamsFromFilters } from 'lib/search/searchUtils'
import type { OfferClickSearchParams } from 'tripPlanner/assistant/contexts/ChatMessageActionsContext'

type AssistantOfferUrlResult =
  | { kind: 'supported'; url: string }
  | { kind: 'unsupported'; reason: 'unsupported_offer_reference_type' }

interface AssistantOfferUrlParams {
  searchParams?: OfferClickSearchParams
  chatId?: number
}

interface AssistantOfferReferenceUrlParams extends AssistantOfferUrlParams {
  offerId: string
  offerType?: string
}

interface AssistantFullOfferUrlParams extends AssistantOfferUrlParams {
  offer: App.AnyOffer
}

function buildAssistantOfferUrlSearchParams({
  searchParams,
  chatId,
}: AssistantOfferUrlParams): URLSearchParams {
  const queryParams = buildSearchParamsFromFilters(searchParams)

  if (searchParams?.flexibleMonths) {
    queryParams.set(queryKeyFlexibleMonths, searchParams.flexibleMonths)
    queryParams.delete(queryKeyCheckIn)
    queryParams.delete(queryKeyCheckOut)
  }
  if (searchParams?.flexibleNights) {
    queryParams.set(queryKeyFlexibleNights, String(searchParams.flexibleNights))
    queryParams.delete(queryKeyCheckIn)
    queryParams.delete(queryKeyCheckOut)
  }

  if (chatId !== undefined) {
    queryParams.set(queryKeyChatId, String(chatId))
  }

  return queryParams
}

function getAccommodationOfferReferenceType(offerType: string | undefined): App.OfferType | undefined {
  if (!offerType || offerType === 'hotel') {
    return 'hotel'
  }

  if (offerType === 'bedbank_hotel') {
    return 'bedbank_hotel'
  }

  return undefined
}

export function buildAssistantOfferUrlForOffer({
  offer,
  searchParams,
  chatId,
}: AssistantFullOfferUrlParams): AssistantOfferUrlResult {
  const queryParams = buildAssistantOfferUrlSearchParams({ searchParams, chatId })

  if (isExperienceOffer(offer)) {
    return { kind: 'supported', url: getExperienceOfferPageURL(offer, queryParams) }
  }

  return { kind: 'supported', url: offerPageURL(offer, queryParams) }
}

export function buildAssistantOfferUrlForReference({
  offerId,
  offerType,
  searchParams,
  chatId,
}: AssistantOfferReferenceUrlParams): AssistantOfferUrlResult {
  const accommodationOfferType = getAccommodationOfferReferenceType(offerType)
  if (!accommodationOfferType) {
    return { kind: 'unsupported', reason: 'unsupported_offer_reference_type' }
  }

  return {
    kind: 'supported',
    url: getHotelOfferUrl(
      offerId,
      accommodationOfferType,
      buildAssistantOfferUrlSearchParams({ searchParams, chatId }),
    ),
  }
}
